import { useState, useEffect } from 'react'
import axios from 'axios'
import { authHeaders } from '../CuentaCompensacion'
import styles from './Modal.module.css'

const API = import.meta.env.VITE_API_GATEWAY_URL

async function uploadDocToS3(file) {
    const ext = file.name.split('.').pop().toLowerCase()
    const ct  = ext === 'pdf' ? 'application/pdf' : 'application/octet-stream'
    const headers = await authHeaders()
    const res = await axios.post(`${API}/upload-url`, {
        fileTypes: ['cc_dim'],
        fileExtensions: { cc_dim: ext },
        contentTypes:   { cc_dim: ct },
    }, { headers })
    const { url, key } = res.data.files.cc_dim
    await axios.put(url, file, { headers: { 'Content-Type': ct } })
    return { key, nombre: file.name }
}

function DeclaracionFormModal({ empresaId, opId, decl, onSaved, onClose, setMsg }) {
    const editing = !!decl

    const [form, setForm] = useState({
        numero_declaracion: '',
        fecha_declaracion:  '',
        valor_aduanas:      '',
        arancel:            '',
        iva:                '',
    })
    const [file,      setFile]      = useState(null)
    const [saving,    setSaving]    = useState(false)
    const [uploading, setUploading] = useState(false)

    useEffect(() => {
        if (decl) {
            setForm({
                numero_declaracion: decl.numero_declaracion || '',
                fecha_declaracion:  decl.fecha_declaracion  || '',
                valor_aduanas:      decl.valor_aduanas != null ? String(decl.valor_aduanas) : '',
                arancel:            decl.arancel        != null ? String(decl.arancel)        : '',
                iva:                decl.iva             != null ? String(decl.iva)             : '',
            })
        }
    }, [decl])

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSaving(true)
        try {
            let documento_s3_key = decl?.documento_s3_key || ''
            let documento_nombre = decl?.documento_nombre || ''

            if (file) {
                setUploading(true)
                const uploaded = await uploadDocToS3(file)
                documento_s3_key = uploaded.key
                documento_nombre = uploaded.nombre
                setUploading(false)
            }

            const headers = await authHeaders()
            const payload = {
                ...form,
                valor_aduanas: parseFloat(form.valor_aduanas) || 0,
                arancel:       parseFloat(form.arancel)       || 0,
                iva:           parseFloat(form.iva)           || 0,
                documento_s3_key,
                documento_nombre,
                dim_datos: decl?.dim_datos || {},
            }

            if (editing) {
                await axios.put(
                    `${API}/cuenta-compensacion/${empresaId}/ops/${opId}/declaraciones/${decl.decl_id}`,
                    payload,
                    { headers }
                )
            } else {
                await axios.post(
                    `${API}/cuenta-compensacion/${empresaId}/ops/${opId}/declaraciones`,
                    payload,
                    { headers }
                )
            }
            onSaved()
        } catch (err) {
            setMsg({ type: 'error', text: 'Error guardando declaración: ' + (err.response?.data?.error || err.message) })
        } finally {
            setSaving(false)
            setUploading(false)
        }
    }

    return (
        <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>{editing ? 'Editar Declaración de Importación' : 'Nueva Declaración de Importación'}</h2>
                    <button className={styles.btnClose} onClick={onClose}>✕</button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {/* Número de declaración */}
                    <div className={styles.fieldFull}>
                        <label>Número de Declaración (DIM)</label>
                        <input
                            value={form.numero_declaracion}
                            onChange={e => set('numero_declaracion', e.target.value)}
                            placeholder="Ej: 482026000187924"
                            autoFocus
                        />
                        <p className={styles.hint}>Número oficial asignado por la DIAN / aduana</p>
                    </div>

                    {/* Fecha */}
                    <div className={styles.field}>
                        <label>Fecha de la Declaración</label>
                        <input
                            type="date"
                            value={form.fecha_declaracion}
                            onChange={e => set('fecha_declaracion', e.target.value)}
                        />
                    </div>

                    {/* Valores */}
                    <div className={styles.fieldGroup}>
                        <div className={styles.field}>
                            <label>Valor en Aduana ($)</label>
                            <input
                                type="number"
                                step="1"
                                min="0"
                                value={form.valor_aduanas}
                                onChange={e => set('valor_aduanas', e.target.value)}
                                placeholder="0"
                            />
                        </div>
                        <div className={styles.field}>
                            <label>Arancel ($)</label>
                            <input
                                type="number"
                                step="1"
                                min="0"
                                value={form.arancel}
                                onChange={e => set('arancel', e.target.value)}
                                placeholder="0"
                            />
                        </div>
                        <div className={styles.field}>
                            <label>IVA ($)</label>
                            <input
                                type="number"
                                step="1"
                                min="0"
                                value={form.iva}
                                onChange={e => set('iva', e.target.value)}
                                placeholder="0"
                            />
                        </div>
                    </div>

                    {/* Documento DIM (adjunto) */}
                    <div className={styles.fieldFull}>
                        <label>Documento DIM (PDF)</label>
                        <div className={styles.fileArea}>
                            <input
                                type="file"
                                id="file-dim"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={e => setFile(e.target.files[0] || null)}
                                className={styles.fileInput}
                            />
                            <label htmlFor="file-dim" className={styles.fileLabel}>
                                {file ? (
                                    <span className={styles.fileSelected}>📎 {file.name}</span>
                                ) : decl?.documento_nombre ? (
                                    <span className={styles.fileExisting}>📄 {decl.documento_nombre} <em>(reemplazar)</em></span>
                                ) : (
                                    <span>📎 Seleccionar declaración de importación (PDF)…</span>
                                )}
                            </label>
                        </div>
                        <p className={styles.hint}>Al guardar, podrás extraer automáticamente los campos de la DIM (Fase 3)</p>
                    </div>

                    <div className={styles.actions}>
                        <button type="button" className={styles.btnCancel} onClick={onClose}>Cancelar</button>
                        <button type="submit" className={styles.btnSave} disabled={saving}>
                            {uploading ? '⬆ Subiendo archivo…' : saving ? 'Guardando…' : editing ? 'Actualizar' : 'Agregar Declaración'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default DeclaracionFormModal
